import {
  DATA_PROCESSING_STEP_TYPE,
  DEBT_DATA_TYPE,
  DEBT_REPORT_GROUP,
  DEBT_SERVICE_TYPE,
  PAYABLE_REPORT_KEY,
  RECEIVABLE_REPORT_KEY,
  RECONCILIATION_STEP,
} from 'features/accounting/debts/types';
import { BankStatementImportBankCodes } from 'features/accounting/constants';
import {
  SHIFT_CLOSING_LOCK_STATUS,
  TICKET_STATUS_KEYWORD,
} from 'features/accounting/types';

/** Labels for debt data processing tabs (Bán / Hoàn) */
export const DebtDataTypeLabels: Record<DEBT_DATA_TYPE, string> = {
  [DEBT_DATA_TYPE.SALES]: 'Dữ liệu bán',
  [DEBT_DATA_TYPE.REFUNDS]: 'Dữ liệu hoàn',
};

/** Labels for debt service types (Vé máy bay, Khách sạn, ...) */
export const DebtServiceTypeLabels: Record<DEBT_SERVICE_TYPE, string> = {
  [DEBT_SERVICE_TYPE.AIRLINES]: 'Vé máy bay',
  [DEBT_SERVICE_TYPE.HOTELS]: 'Khách sạn',
  [DEBT_SERVICE_TYPE.ATTRACTIONS]: 'Vé vui chơi',
  [DEBT_SERVICE_TYPE.INSURANCE]: 'Bảo hiểm',
};

/** Labels for report group tabs (Phải thu / Phải trả) */
export const DebtReportGroupLabels: Record<DEBT_REPORT_GROUP, string> = {
  [DEBT_REPORT_GROUP.RECEIVABLES]: 'Phải thu',
  [DEBT_REPORT_GROUP.PAYABLES]: 'Phải trả',
};

/** Labels for receivables report sub-tabs */
export const ReceivableReportLabels: Record<RECEIVABLE_REPORT_KEY, string> = {
  [RECEIVABLE_REPORT_KEY.SALES_REPORT]: 'Báo cáo doanh số',
  [RECEIVABLE_REPORT_KEY.TRANSACTION_REPORT]: 'Báo cáo giao dịch',
  [RECEIVABLE_REPORT_KEY.DEBT_REPORT]: 'Báo cáo công nợ',
};

/** Labels for payables report sub-tabs */
export const PayableReportLabels: Record<PAYABLE_REPORT_KEY, string> = {
  [PAYABLE_REPORT_KEY.PROVIDER_TOPUP]: 'Báo cáo giao dịch Nhà cung cấp',
  [PAYABLE_REPORT_KEY.DEBT_REPORT]: 'Báo cáo công nợ Nhà cung cấp',
};

/** Titles for data reconciliation wizard steps */
export const ReconciliationStepTitles: Record<RECONCILIATION_STEP, string> = {
  [RECONCILIATION_STEP.IMPORT]: 'Import dữ liệu',
  [RECONCILIATION_STEP.VERIFY]: 'Đối soát',
  [RECONCILIATION_STEP.DATA_PROCESSING]: 'Xử lý dữ liệu',
};

export const ProcessingTypeToStep: Record<
  DATA_PROCESSING_STEP_TYPE | string,
  RECONCILIATION_STEP
> = {
  [DATA_PROCESSING_STEP_TYPE.IMPORT]: RECONCILIATION_STEP.IMPORT,
  [DATA_PROCESSING_STEP_TYPE.RECONCILIATION]: RECONCILIATION_STEP.VERIFY,
  [DATA_PROCESSING_STEP_TYPE.PROCESSING]: RECONCILIATION_STEP.DATA_PROCESSING,
};

export const StepToProcessingType: Record<
  RECONCILIATION_STEP,
  DATA_PROCESSING_STEP_TYPE
> = {
  [RECONCILIATION_STEP.IMPORT]: DATA_PROCESSING_STEP_TYPE.IMPORT,
  [RECONCILIATION_STEP.VERIFY]: DATA_PROCESSING_STEP_TYPE.RECONCILIATION,
  [RECONCILIATION_STEP.DATA_PROCESSING]: DATA_PROCESSING_STEP_TYPE.PROCESSING,
};

export const TOTAL_RECONCILIATION_STEPS = 3;

/**
 * Ticket status forced by each data tab of the pending data ("Dữ liệu treo"): the refunds tab only lists `REFUND`
 * tickets, as the old "Báo cáo hoàn" page did (the sales tab keeps the "Loại vé" filter free, as "Báo cáo bán").
 */
export const PendingDebtFixedTicketStatus: Partial<
  Record<DEBT_DATA_TYPE, TICKET_STATUS_KEYWORD>
> = Object.freeze({
  [DEBT_DATA_TYPE.REFUNDS]: TICKET_STATUS_KEYWORD.REFUND,
});

/**
 * Lock state of the tickets listed by the "Xử lý dữ liệu" step: the unconfirmed tickets, as the old "Báo cáo bán"
 * page (`IsLocks = 1`).
 */
export const DataProcessingStepLockStatus =
  SHIFT_CLOSING_LOCK_STATUS.UNCONFIRMED;

/** Title of the filter drawer of each data tab. */
export const PendingDebtFilterDrawerTitles: Record<DEBT_DATA_TYPE, string> = {
  [DEBT_DATA_TYPE.SALES]: 'Bộ lọc dữ liệu vé máy bay',
  [DEBT_DATA_TYPE.REFUNDS]: 'Bộ lọc dữ liệu hoàn',
};

/** Wording of the row delete confirmation / success message of each data tab. */
export const PendingDebtDeleteRecordLabels: Record<DEBT_DATA_TYPE, string> = {
  [DEBT_DATA_TYPE.SALES]: 'bản ghi vé',
  [DEBT_DATA_TYPE.REFUNDS]: 'bản ghi vé hoàn',
};

/** Excel export of each data tab: sheet name and file name prefix (`<prefix>_<timestamp>.xlsx`). */
export const PendingDebtExportSheetNames: Record<DEBT_DATA_TYPE, string> = {
  [DEBT_DATA_TYPE.SALES]: 'Dữ liệu treo - Bán',
  [DEBT_DATA_TYPE.REFUNDS]: 'Dữ liệu treo - Hoàn',
};
export const PendingDebtExportFilePrefixes: Record<DEBT_DATA_TYPE, string> = {
  [DEBT_DATA_TYPE.SALES]: 'DuLieuTreoBan',
  [DEBT_DATA_TYPE.REFUNDS]: 'DuLieuTreoHoan',
};

/** Page size used to load every ticket of the Excel export (same as the sales report export). */
export const PendingDebtExportPageSize = 1000;

/** Safety cap on the number of pages loaded by the Excel export. */
export const PendingDebtExportMaxPages = 100;

/**
 * Messages of the import step ("Import dữ liệu") of "Xử lý dữ liệu", worded as the old "Đối soát dữ liệu" modal.
 */
export const DataImportMessages = Object.freeze({
  MISSING_AIRLINE: 'Vui lòng chọn hãng hàng không!',
  MISSING_RECONCILE_DATE: 'Vui lòng chọn ngày đối soát!',
  MISSING_BANK_ACCOUNT: 'Vui lòng chọn tài khoản ngân hàng!',
  MISSING_RECONCILE_DATE_RANGE: 'Vui lòng chọn khoảng ngày đối soát!',
  UNSUPPORTED_BANK_STATEMENT: `Chưa hỗ trợ import sao kê của ngân hàng này (chỉ hỗ trợ ${BankStatementImportBankCodes.join(', ')}).`,
  UNSUPPORTED_AIRLINE_IMPORT:
    'Chưa hỗ trợ import file của hãng này, vui lòng bỏ file để chỉ đối soát dữ liệu đã có.',
  IMPORT_SUCCESS: 'Import thành công!',
  IMPORT_FAILURE: 'Import thất bại!',
  COMPARE_SUCCESS: 'Đối soát thành công!',
  COMPARE_EMPTY: 'Không có dữ liệu đối soát!',
});

/** Count labels of the comparison success message ("Đối soát thành công! Khớp: 3, Lệch: 1"), as the old modal. */
export const CompareSuccessCountLabels = Object.freeze({
  MATCHED: 'Khớp',
  UNMATCHED: 'Lệch',
  UNCONFIRMED: 'Chưa xác nhận',
});

/** Days added to the last day of a reconcile range: the bank comparison range is `[from, to)`. */
export const ReconcileRangeEndOffsetDays = 1;

/** Days before today of the default reconcile day (the provider date and the bank range of "Xử lý dữ liệu"). */
export const DefaultReconcileDayOffsetDays = 1;

/** Number of files kept by the import uploaders: the import takes a single Excel file. */
export const ImportUploadMaxFiles = 1;
